import { useNavigate } from 'react-router';
import { Center, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineChevronRightIcon, SolidHeartIcon, SolidShieldCheckIcon, SolidStarIcon } from '@tempmee/react-icons';
import { Divider, ZStack } from '~/commonComponents';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import HY_Tooth from '~/images/HY_tooth.png';
import { IJob, IUser } from '~/models';
import { TNullable } from '~/types/Nullable';
import { CompanyMatchLabel } from '../CompanyMatchLabel/CompanyMatchLabel';

interface IProfessionalProfileProps {
  job?: TNullable<IJob>;
  professional: TNullable<IUser>;
  disableNavigation?: boolean;
  hasBackgroundCheck?: boolean;
  hasWorkedWithDSO?: boolean;
  hasWorkedWithOffice?: boolean;
  displayProfession?: string;
  onTrackOverviewClick?: () => void;
}

export const ProfessionalProfile = ({
  job,
  professional,
  disableNavigation = false,
  hasBackgroundCheck = false,
  hasWorkedWithDSO = false,
  hasWorkedWithOffice = false,
  displayProfession,
  onTrackOverviewClick,
}: IProfessionalProfileProps) => {
  const navigate = useNavigate();

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  const isFavorite = professional?.isFavorite;
  const hasProfileImage = !!professional?.profile_image;

  const handleNavigation = () => {
    if (!job || !professional) return;

    onTrackOverviewClick?.();

    if (isEnablePermanentJobM2M && job.isOnsiteInterview) {
      navigate(`/permanent-jobs/${job.postingId}/candidates/${professional.id}/overview`);
    } else {
      navigate(`/dashboard/candidate/${job.id}`, {
        state: {
          candidateId: professional?.id,
          state: job?.state,
          profession: job?.profession,
          subProfession: job?.subProfession,
        },
      });
    }
  };

  return (
    <Row align="center" gap={5}>
      <Center
        direction="column"
        style={{
          position: 'relative',
          width: 104,
          height: 104,
          padding: 2,
          borderRadius: '100%',
          border: `3px solid ${isFavorite ? colors.favorites[400] : 'transparent'}`,
        }}
      >
        {hasProfileImage ? (
          <img
            src={professional.profile_image}
            style={{
              borderRadius: 104,
              height: 104,
              objectFit: 'cover',
              width: 104,
            }}
            alt="profile_image"
          />
        ) : (
          <Column className="default_circle_container">
            <img src={HY_Tooth} alt="profile_image" width="104px" />
          </Column>
        )}
        {isFavorite && (
          <ZStack right={-4} top="72%">
            <SolidHeartIcon color={colors.favorites[400]} />
          </ZStack>
        )}
      </Center>

      <Column style={{ marginBottom: 20 }}>
        <Row align="center" className="flex-1" gap={2} style={{ marginTop: 15 }}>
          <Typography
            as="span"
            bold
            size="heading_4"
            numberOfLines={1}
            style={{ maxWidth: 280 }}
          >
            {professional?.full_name}
          </Typography>

          <Row align="center" gap={1}>
            <SolidStarIcon size={24} color={colors.success[500]} />
            <Typography as="span" bold size="heading_4">
              {professional?.average_rating?.toFixed(1) || '5.0'}
            </Typography>
          </Row>

          {!disableNavigation && (
            <>
              <Divider color={colors.neutral[600]} height={24} marginVertical={0} width={1.5} />
              <Row align="center" gap={2} onClick={handleNavigation} style={{ cursor: 'pointer' }}>
                <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
                  {isEnablePermanentJobM2M && job?.isOnsiteInterview ? 'Overview' : 'Profile'}
                </Typography>
                <OutlineChevronRightIcon size={24} />
              </Row>
            </>
          )}
        </Row>
        <Typography as="span">Drivers License Verified</Typography>
        <Row align="center" gap={1} style={{ marginBottom: 8 }}>
          <Typography as="span">{displayProfession} Credentials Verified</Typography>
          {hasBackgroundCheck && (
            <>
              <Divider color={colors.neutral[600]} height={16} marginVertical={0} width={1.5} />
              <Row align="center" gap={1}>
                <Typography as="span">Background Checked</Typography>
                <SolidShieldCheckIcon size={24} color={colors.secondary[500]} />
              </Row>
            </>
          )}
        </Row>
        <CompanyMatchLabel
          offerOwnerId={job?.offer_owner.id || ''}
          professional={professional}
          hasWorkedWithDSO={hasWorkedWithDSO}
          hasWorkedWithOffice={hasWorkedWithOffice}
        />
      </Column>
    </Row>
  );
};
